import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Brain, Eye, EyeSlash } from '@phosphor-icons/react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2">
            <Brain size={40} weight="duotone" className="text-[#007AFF]" />
            <span className="font-heading font-black text-2xl tracking-tight text-white uppercase">
              Trader Reset AI
            </span>
          </Link>
        </div>

        {/* Form Card */}
        <div className="bg-[#131313] border border-white/10 p-8" data-testid="login-form">
          <h1 className="font-heading font-bold text-2xl uppercase text-white mb-2 text-center">
            Welcome Back
          </h1>
          <p className="text-[#8892B0] text-center mb-8">
            Sign in to continue your trading journey
          </p>

          {error && (
            <div className="mb-6 p-4 bg-[#FF3B30]/10 border border-[#FF3B30]/30 text-[#FF3B30] text-sm" data-testid="login-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email" className="text-xs text-[#8892B0] uppercase tracking-wider mb-2 block">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-transparent border-white/20 text-white focus:border-[#007AFF] rounded-none p-4"
                placeholder="trader@example.com"
                required
                data-testid="login-email"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-xs text-[#8892B0] uppercase tracking-wider mb-2 block">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-transparent border-white/20 text-white focus:border-[#007AFF] rounded-none p-4 pr-12"
                  placeholder="Enter your password"
                  required
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#8892B0] hover:text-white"
                >
                  {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-[#007AFF] text-white font-bold uppercase tracking-widest hover:bg-[#005bb5] transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              data-testid="login-submit"
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </button>

            <div className="text-right mt-3">
              <Link to="/forgot-password" className="text-sm text-[#8892B0] hover:text-[#007AFF] transition-colors" data-testid="forgot-password-link">
                Forgot password?
              </Link>
            </div>
          </form>

          <p className="mt-6 text-center text-[#8892B0]">
            Don't have an account?{' '}
            <Link to="/register" className="text-[#007AFF] hover:underline" data-testid="login-register-link">
              Create one
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
